import api from "./api";

// All paths are relative to REACT_APP_API_URL set in .env. The axios instance
// in services/api.js already handles Authorization header + token refresh.

// ---------- PROGRAM CRUD ----------
export const listPrograms = (params = {}) =>
  api.get("/programs", { params }).then((r) => r.data);

export const getProgram = (programId) =>
  api.get(`/programs/${programId}`).then((r) => r.data);

export const createProgram = (payload) =>
  api.post("/programs", payload).then((r) => r.data);

export const updateProgram = (programId, payload) =>
  api.put(`/programs/${programId}`, payload).then((r) => r.data);

export const deleteProgram = (programId) =>
  api.delete(`/programs/${programId}`).then((r) => r.data);

// ---------- PROGRAM OUTCOMES ----------
export const listPOs = (programId) =>
  api.get(`/programs/${programId}/pos`).then((r) => r.data);
export const createPO = (programId, payload) =>
  api.post(`/programs/${programId}/pos`, payload).then((r) => r.data);
export const updatePO = (programId, poId, payload) =>
  api.put(`/programs/${programId}/pos/${poId}`, payload).then((r) => r.data);
export const deletePO = (programId, poId) =>
  api.delete(`/programs/${programId}/pos/${poId}`).then((r) => r.data);
export const seedStandardPOs = (programId) =>
  api.post(`/programs/${programId}/pos/seed-standard`).then((r) => r.data);

// ---------- PROGRAM SPECIFIC OUTCOMES ----------
export const listPSOs = (programId) =>
  api.get(`/programs/${programId}/psos`).then((r) => r.data);
export const createPSO = (programId, payload) =>
  api.post(`/programs/${programId}/psos`, payload).then((r) => r.data);
export const updatePSO = (programId, psoId, payload) =>
  api.put(`/programs/${programId}/psos/${psoId}`, payload).then((r) => r.data);
export const deletePSO = (programId, psoId) =>
  api.delete(`/programs/${programId}/psos/${psoId}`).then((r) => r.data);

// ---------- PEOs ----------
export const listPEOs = (programId) =>
  api.get(`/programs/${programId}/peos`).then((r) => r.data);
export const createPEO = (programId, payload) =>
  api.post(`/programs/${programId}/peos`, payload).then((r) => r.data);
export const updatePEO = (programId, peoId, payload) =>
  api.put(`/programs/${programId}/peos/${peoId}`, payload).then((r) => r.data);
export const deletePEO = (programId, peoId) =>
  api.delete(`/programs/${programId}/peos/${peoId}`).then((r) => r.data);

// ---------- ATTAINMENT TARGETS ----------
export const listTargets = (programId) =>
  api.get(`/programs/${programId}/attainment-targets`).then((r) => r.data);
export const createTarget = (programId, payload) =>
  api
    .post(`/programs/${programId}/attainment-targets`, payload)
    .then((r) => r.data);
export const updateTarget = (programId, targetId, payload) =>
  api
    .put(`/programs/${programId}/attainment-targets/${targetId}`, payload)
    .then((r) => r.data);
export const deleteTarget = (programId, targetId) =>
  api
    .delete(`/programs/${programId}/attainment-targets/${targetId}`)
    .then((r) => r.data);
export const seedDefaultTargets = (programId, payload = {}) =>
  api
    .post(`/programs/${programId}/attainment-targets/seed-defaults`, payload)
    .then((r) => r.data);

// ---------- COURSES IN PROGRAM ----------
export const listCoursesInProgram = (programId) =>
  api.get(`/programs/${programId}/courses`).then((r) => r.data);
export const assignCourseToProgram = (programId, courseId) =>
  api
    .put(`/programs/${programId}/courses/${courseId}`)
    .then((r) => r.data);
export const unassignCourseFromProgram = (programId, courseId) =>
  api
    .delete(`/programs/${programId}/courses/${courseId}`)
    .then((r) => r.data);

// Admin: create a brand-new course pre-assigned to this program.
export const createCourseInProgram = (programId, payload) =>
  api
    .post(`/programs/${programId}/courses/create`, payload)
    .then((r) => r.data);

// ---------- ADMIN PICKER HELPERS ----------
export const listTeachersForPicker = () =>
  api.get(`/programs/_picker/teachers`).then((r) => r.data);
export const listSemestersForPicker = () =>
  api.get(`/programs/_picker/semesters`).then((r) => r.data);

// ---------- ADMIN: ALL COURSES (existing /api/admin/courses/all) ----------
// Returns the full course list across the institution, not scoped to a teacher.
// Used by the "Available to assign" picker in CoursesTab.
export const listAllCoursesAsAdmin = (params = {}) =>
  api
    .get(`/admin/courses/all`, { params: { limit: 200, ...params } })
    .then((r) => r.data);

// ---------- COURSE OUTCOMES V2 (Phase 2a) ----------
export const listCOsByCourse = (courseId) =>
  api.get(`/course-outcomes-v2/courses/${courseId}`).then((r) => r.data);

export const createCO = (courseId, payload) =>
  api
    .post(`/course-outcomes-v2/courses/${courseId}`, payload)
    .then((r) => r.data);

export const updateCO = (coId, payload) =>
  api.put(`/course-outcomes-v2/${coId}`, payload).then((r) => r.data);

export const deleteCO = (coId) =>
  api.delete(`/course-outcomes-v2/${coId}`).then((r) => r.data);

export const bulkCreateCOs = (courseId, outcomes) =>
  api
    .post(`/course-outcomes-v2/courses/${courseId}/bulk`, { outcomes })
    .then((r) => r.data);

// ---------- CO-PO MAPPINGS (Phase 2b) ----------
export const getCOPOMatrix = (courseId) =>
  api
    .get(`/co-po-mappings/courses/${courseId}/matrix`)
    .then((r) => r.data);

export const bulkUpsertMappings = (
  courseId,
  mappings,
  deleteOthers = false
) =>
  api
    .post(`/co-po-mappings/courses/${courseId}/bulk`, {
      mappings,
      deleteOthers,
    })
    .then((r) => r.data);

export const updateMapping = (mappingId, payload) =>
  api.put(`/co-po-mappings/${mappingId}`, payload).then((r) => r.data);

export const deleteMapping = (mappingId) =>
  api.delete(`/co-po-mappings/${mappingId}`).then((r) => r.data);

// ---------- AI SUGGESTIONS (Phase 2c) ----------
export const suggestCOs = (payload) =>
  api.post(`/ai/suggest-cos`, payload).then((r) => r.data);

export const suggestCoPoJustification = (payload) =>
  api.post(`/ai/suggest-co-po-justification`, payload).then((r) => r.data);

// ---------- AI QUIZ + ASSIGNMENT PUBLISH (Phase 3) ----------
export const generateQuestions = (payload) =>
  api.post(`/ai/generate-questions`, payload).then((r) => r.data);

export const createAssignmentFromAI = (courseId, payload) =>
  api
    .post(`/assignment/courses/${courseId}/assignments/from-ai`, payload)
    .then((r) => r.data);

// ---------- ATTAINMENT SNAPSHOTS / TREND (Phase 2.2) ----------
export const lockAttainmentSnapshot = (programId, academicYear) =>
  api.post(`/attainment/programs/${programId}/snapshot`, { academicYear }).then((r) => r.data);

export const getAttainmentTrend = (programId) =>
  api.get(`/attainment/programs/${programId}/trend`).then((r) => r.data);

// Returns a Blob — caller should create an object URL and trigger download.
export const exportAttainmentXlsx = (programId) =>
  api
    .get(`/attainment/programs/${programId}/export?format=xlsx`, { responseType: "blob" })
    .then((r) => r.data);

// SAR Word export (.docx) — NBA Criteria 2/3/8 document
export const exportSARDocx = (programId) =>
  api
    .get(`/attainment/programs/${programId}/sar`, { responseType: "blob" })
    .then((r) => r.data);

// ---------- AI ATR DRAFT (Phase 4.3) ----------
// payload: { po_code, po_statement, observed_level, target_level, contributing_cos }
// returns: { observation, root_cause, planned_action, _mock? }
export const suggestATRDraft = (payload) =>
  api.post(`/ai/suggest-atr`, payload).then((r) => r.data);

// ---------- INDIRECT SURVEYS (Phase 2.1) ----------
export const getSurveyStatus = (courseId) =>
  api.get(`/surveys/courses/${courseId}/status`).then((r) => r.data);

export const submitSurvey = (courseId, responses) =>
  api.post(`/surveys/courses/${courseId}`, { responses }).then((r) => r.data);

export const getSurveyAggregate = (courseId) =>
  api.get(`/surveys/courses/${courseId}/aggregate`).then((r) => r.data);

// ---------- ENDTERM CO ALLOCATION (Phase 1.2) ----------
export const getEndTermConfig = (courseId) =>
  api.get(`/endterm-config/courses/${courseId}`).then((r) => r.data);

export const saveEndTermConfig = (courseId, payload) =>
  api.put(`/endterm-config/courses/${courseId}`, payload).then((r) => r.data);

// ---------- ATTAINMENT CONFIG ----------
export const getAttainmentConfig = (programId) =>
  api.get(`/attainment-config/programs/${programId}`).then((r) => r.data);

export const saveAttainmentConfig = (programId, payload) =>
  api.put(`/attainment-config/programs/${programId}`, payload).then((r) => r.data);

export const seedAttainmentConfigDefaults = (programId) =>
  api.post(`/attainment-config/programs/${programId}/seed-defaults`).then((r) => r.data);

// ---------- ATTAINMENT (Epic 4) ----------
export const getCourseAttainment = (courseId) =>
  api.get(`/attainment/courses/${courseId}`).then((r) => r.data);

export const getProgramAttainment = (programId) =>
  api.get(`/attainment/programs/${programId}`).then((r) => r.data);

// ---------- ATR (Epic 6a) ----------
export const listATRs = (programId, params = {}) =>
  api.get(`/atr/programs/${programId}`, { params }).then((r) => r.data);

export const createATR = (programId, payload) =>
  api.post(`/atr/programs/${programId}`, payload).then((r) => r.data);

export const getATR = (atrId) =>
  api.get(`/atr/${atrId}`).then((r) => r.data);

export const updateATR = (atrId, payload) =>
  api.put(`/atr/${atrId}`, payload).then((r) => r.data);

export const closeATR = (atrId, payload) =>
  api.post(`/atr/${atrId}/close`, payload).then((r) => r.data);

export const deleteATR = (atrId) =>
  api.delete(`/atr/${atrId}`).then((r) => r.data);

// ---------- GRADING SPLITS (Epic 6b) ----------
export const getMarksSplit = (courseId) =>
  api.get(`/grading-splits/courses/${courseId}`).then((r) => r.data);

export const bulkUpdateMarksSplit = (courseId, splits) =>
  api
    .put(`/grading-splits/courses/${courseId}/bulk`, { splits })
    .then((r) => r.data);
